"use client";
import {useMemo, useEffect, useState, useRef} from "react";

import '../../../assets/styles/scss/_userDeal.scss'
import awsconfig from '../../../awsconfig';

import {fetchAuthSession, confirmSignIn, signIn} from 'aws-amplify/auth';
import {useSearchParams} from "next/navigation";
import UserDealNavbar from "components/Deals/DealNavBar";
import UserDeals from "components/Deals/DealsView";
import {Amplify} from 'aws-amplify';
import Loader from "components/Preloader/Loader";
import ThemeModifier from "components/Layout/ThemeModifier";
import {isPostHogActive} from "utils";
import {usePostHog} from "posthog-js/react";
import Footer from "components/Layout/Footer";
import ScrollUp from "components/ScrollUp/ScrollUp";
import {useConfig} from "components/ConfigProvider";

Amplify.configure(awsconfig);

const PAGE_SIZE = 40;

export default function DealsContent({siteConfig, questions, topRatedProducts, initialSearch = '', categories, pageConfig}) {
    const posthog = usePostHog();
    const config = useConfig()
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const searchParams = useSearchParams();
    const [user, setUser] = useState(null);
    const [open, setOpen] = useState(false);

    const [selectedNav, setSelectedNav] = useState(initialSearch ? null : 'Featured Products');
    const [preferredCategories, setPreferredCategories] = useState([]);
    const [preference, setPreference] = useState({});
    const [questionaireModal, setQuestionaireModal] = useState(false);
    const [isDealLoading, setDealLoading] = useState(false);
    const [totalActivePromotions, setTotalActivePromotions] = useState(0);
    const [isPreDealLoading, setPreDealLoading] = useState(false);
    const [resultType, setResultType] = useState("filter")
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const isFetchingMore = useRef(false);
    const [refresh, setRefresh] = useState(false);
    const [inputValue, setInputValue] = useState(initialSearch);


    useEffect(() => {
        const signOut = async () => {
            //TODO: what does this even mean?
        }

        const signInAndConfirm = async (email, token) => {
            await signIn({
                username: email,
                options: {
                    authFlowType: "CUSTOM_WITHOUT_SRP",
                    preferredChallenge: "EMAIL_OTP"
                }
            });
            await confirmSignIn({
                challengeResponse: token
            });
        };

        const getUserPreferences = async (email) => {
            const res = await fetch(`${config.siteApi.apiUrl}/crm/user?email=${encodeURIComponent(email)}`);

            if (res.ok) {
                let data = await res.json();
                setPreference(data.data.preference || {})
                setPreferredCategories(data.data.preference ? JSON.parse(data.data.preference.categories) : []);

                return data.data.preference;
            }

            return {};
        };

        const getCurrentUser = async () => {
            const session = await fetchAuthSession();
            return session.tokens?.idToken.payload;
        };

        const getUser = async () => {
            try {
                let currentUser = await getCurrentUser();
                const email = searchParams.get("email");
                const token = searchParams.get("token");
                if (!currentUser) {
                    if (email && token) {
                        await signInAndConfirm(email, token);
                        currentUser = await getCurrentUser();
                    } else {
                        // router.push('/landing/deals');
                        return;
                    }
                } else if (email !== currentUser.email && token) {
                    await signOut();
                    await signInAndConfirm(email, token);
                    currentUser = await getCurrentUser();
                }
                return currentUser;
            } catch (error) {
                console.error("Error fetching user:", error);
            } finally {
                setLoading(false);
            }
        };

        const getUserDeals = async () => {
            const currentUser = await getUser();

            setPreDealLoading(true);

            let pref = {};
            if (currentUser) {
                setUser(currentUser);
                pref = await getUserPreferences(currentUser.email)
            }

            if (!initialSearch && (!currentUser || !Object.keys(pref).length)) {
                setSelectedNav('Featured Products');
            }

            setPreDealLoading(false)
        }

        if (!questionaireModal && categories && config) {
            getUserDeals();
        }
    }, [questionaireModal, categories, config]);

    useEffect(() => {
        if (initialSearch) {
            fetchDeals(initialSearch);
        }
    }, [initialSearch]);

    useEffect(() => {
        if (!selectedNav) {
            return;
        }
        setDeals([]);
        fetchDeals(null, selectedNav, preference?.shopping_audience, preference?.is_amazon_prime)
    }, [selectedNav, preference, refresh])

    const userCategories = useMemo(()=>{
        if(user && preference && preferredCategories){
            return ['Your Categories', ...categories, ...preferredCategories]
        }

        return ['Featured Products', ...categories]
    }, [user, preference,categories, preferredCategories])

    const fetchDeals = async (search, categories = [], shoppingAudience, isPrime, page = 1) => {
        if (!config) return;
        categories = Array.isArray(categories) ? categories : [categories];

        if(categories[0] === 'Featured Products' && categories.length === 1 && page === 1) {
            setDeals(topRatedProducts?.data);
            setTotalActivePromotions(topRatedProducts?.totalActivePromotionsCount);
            setTotalCount(topRatedProducts?.totalCount || 0);
            setResultType("filter");
            return;
        }

        if (isFetchingMore.current) {
            return;
        }

        isFetchingMore.current = true;

        let parsedResponse = {data: []};
        try {
            let url = `${config.siteApi.apiUrl}/crm/deals`;
            const postHogId =  posthog.get_distinct_id();
            const queryParams = new URLSearchParams();

            if(search && search.length) {
                queryParams.append('search', search);
                if (isPostHogActive()) {
                    posthog.capture("search", {"keyword": search});
                }
            }

            if (categories?.length) {
                queryParams.append('categories', categories.join(','));
            }

            if (shoppingAudience) {
                queryParams.append('shoppingAudience', shoppingAudience);
            }

            if (isPrime) {
                queryParams.append('isPrime', isPrime);
            }

            queryParams.append('offset', (page - 1) * PAGE_SIZE);
            queryParams.append('limit', PAGE_SIZE);
            setDealLoading(true);
            if(page === 1){
                setDeals([])
            }

            const response = await fetch(`${url}?${queryParams}`, {
                method: "GET",
                headers: {
                    'x-poln-user-id': postHogId
                },
            });

            if (!response.ok) {
                throw new Error();
            }

            parsedResponse = await response.json();

            if (totalCount !== parsedResponse.totalCount) {
                setTotalCount(parsedResponse.totalCount)
            }
            if (page > 1) {
                setDeals(prev => [...prev, ...parsedResponse.data]);
            } else {
                setDeals(parsedResponse.data);
            }

            setResultType(parsedResponse.resultType)
            setTotalActivePromotions(parsedResponse.totalActivePromotionsCount)
        } catch (error) {
            console.error("Failed to fetch deals:", error);
            setDeals([]);
            setTotalCount(0);
            setTotalActivePromotions(0);
        } finally {
            setDealLoading(false);
            isFetchingMore.current = false;
        }

        return parsedResponse.data;
    };

    const deleteDeal = async (productId, promoCode) => {
        if (!config) return;
        await fetch(`${config.siteApi.apiUrl}/crm/deal`, {
            method: 'PUT',
            body: JSON.stringify({
                productId,
                promoCode,
                delete: true
            })
        });
    }

    const updateRecommendation = async (productId, promoCode, recommended) => {
        if (!config) return;
        await fetch(`${config.siteApi.apiUrl}/crm/deal`, {
            method: 'PUT',
            body: JSON.stringify({
                productId,
                promoCode,
                key: 'is_recommended',
                value: recommended
            })
        });

        setDeals(prevDeals => {
            return prevDeals.map(item =>
                (item.productId === productId && item.promoCode === promoCode) ? {
                    ...item,
                    isRecommended: recommended
                } : item
            );
        });
    };

    const reset = async () => {
        // Check if user and preferences are available
        const hasUserAndPreferences = user && preference && Object.keys(preference).length > 0;

        // Reset search state
        setTotalCount(0);
        setDeals([]);
        setPage(1);
        setResultType("filter")

        // Set selected nav based on user/preferences status
        if (hasUserAndPreferences) {
            setSelectedNav('Your Categories');
        } else {
            if(selectedNav === 'Featured Products') {
                setRefresh(!refresh)
            }else {
                setSelectedNav('Featured Products');
            }
        }
    }

    const handleViewMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchDeals(null, selectedNav, preference?.shopping_audience, preference?.is_amazon_prime, nextPage)
    };

    if (!categories || categories.length === 0) {
        return null
    }

    return (
        <div className="light-theme-background" style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh'
        }}>
            <ThemeModifier siteConfig={siteConfig}/>
            <div className="search-header-section">
                <UserDealNavbar
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    logo={siteConfig.logo}
                    user={user} questions={questions} questionaireModal={questionaireModal}
                    setQuestionaireModal={setQuestionaireModal} setLoading={setDealLoading}
                    reset={reset}
                    initialSearch={initialSearch}
                    doSearch={fetchDeals}
                    pageConfig={pageConfig}
                />
            </div>
            <div className="deals-content-wrapper">
                <div className="user-deal-item-container">
                    <UserDeals
                        inputValue={inputValue}
                        resultType={resultType}
                        totalCount={totalCount}
                        deals={deals}
                        user={user}
                        deleteDeal={deleteDeal}
                        categories={userCategories}
                        selectedNav={selectedNav}
                        setSelectedNav={setSelectedNav}
                        setQuestionaireModal={setQuestionaireModal}
                        questions={questions}
                        questionaireModal={questionaireModal}
                        updateRecommendation={updateRecommendation}
                        totalActivePromotions={totalActivePromotions}
                        isLoading={loading || isDealLoading}
                        onViewMore={handleViewMore}
                        isDealLoading={isDealLoading}
                        hasMoreDeals={(page * PAGE_SIZE) < totalCount}
                    />
                </div>
                {loading &&
                    <Loader isLoaded={!loading} siteConfig={siteConfig}/>
                }
            </div>
            <Footer name="deals" siteConfig={siteConfig}/>
            <ScrollUp/>
        </div>
    );
}
